import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError, distinctUntilChanged, map, Observable, of, Subject, switchMap, takeWhile,
  tap, throwError, timer, throwError as observableThrowError,
} from 'rxjs';
import { JsonService } from 'src/app/services/core/json.service';
import { VendorQuotesApiResources } from './vendor-quotes.constant';
import {
  ExtractionStatus, ProposalLineState, QuoteOption, QuoteSummary, ServerApplyPlan, VendorQuoteApplication,
  VendorQuoteAppliedRow, VendorQuoteApplyRequest, VendorQuoteApplyResult, VendorQuoteDraft, VendorQuoteTarget,
} from './vendor-quote-flow.types';

@Injectable({
  providedIn: 'root'
})
export class VendorQuoteDataService {
  private vendorQuoteListUpdate = new Subject<any>();
  
  private baseURL;

  constructor(
    private http: HttpClient,
    private jsonService: JsonService) {
    this.baseURL = VendorQuotesApiResources;
  }
  setVendorQuoteUpdate(data: any) {
    this.vendorQuoteListUpdate.next(data);
  }

  getVendorQuoteUpdateList(): Observable<any> {
    return this.vendorQuoteListUpdate.asObservable();
  }

  search(model: any): Observable<any> {
    const endPoint = `${this.baseURL.search}/${model.moduleId}/${model.referenceId}`;
    return this.http.get<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  /* ── Upload Vendor Quote flow ─────────────────────────────────────────────
     Live against the backend. The draft endpoints are all nested under a bid, so every call
     needs the bid id — a draft id on its own is never enough to reach a row. */

  /** Id of the quote saved last, so the Vendor Quotes tab can show the saved banner (A4). */
  justSavedId: number | null = null;

  /** How often to ask while a document is being read; the server also returns `pollAfterMs`. */
  private static readonly POLL_MS = 1500;

  /**
   * Unwraps the API envelope.
   *
   * `HttpResponse::setResponse()` wraps whatever a controller returns in `{ data: … }`, and these
   * controllers return `{ success, message, data: <resource> }` — so the payload actually arrives
   * DOUBLE-nested as `{ data: { success, message, data: <resource> } }`. Endpoints that return a
   * Resource directly are nested only once. Reading the wrong depth yields `undefined`, which is
   * how a draft id became `NaN` in the polling URL.
   *
   * Same shape the rest of the module already handles with `response.data?.data || response.data`;
   * written once here so every method agrees.
   */
  private static unwrap<T = any>(res: any): T {
    return (res?.data?.data ?? res?.data ?? res) as T;
  }

  /**
   * Starts reading an uploaded file and emits each reading stage (B3), then the finished draft.
   *
   * POST creates the draft, then the status endpoint is polled until the read finishes. Stages are
   * emitted as they change so the dialog's checklist ticks; the full draft is fetched once, at the
   * end, because the slim status payload deliberately omits the lines.
   */
  startExtraction(bidId: number, attachFileId: number | null): Observable<{ stage: ExtractionStatus; draft?: VendorQuoteDraft }> {
    return this.http
      .post<any>(this.baseURL.extractions(bidId), { attach_file_id: attachFileId })
      .pipe(
        switchMap(res => this.pollUntilDone(bidId, Number(VendorQuoteDataService.unwrap(res)?.id))),
        catchError(this.errorHandlerMessage),
      );
  }

  /**
   * Emits one event per stage change until the read stops, then the full draft.
   *
   * Errors rather than completing on a failed read: the dialog's `error` branch is what shows the
   * user that nothing was kept, and a silent completion would leave the checklist frozen.
   */
  private pollUntilDone(bidId: number, id: number): Observable<{ stage: ExtractionStatus; draft?: VendorQuoteDraft }> {
    return timer(0, VendorQuoteDataService.POLL_MS).pipe(
      switchMap(() => this.http.get<any>(this.baseURL.extractionStatus(bidId, id))),
      map(res => VendorQuoteDataService.unwrap(res)),
      takeWhile(s => !!s && s.isProcessing, true),
      distinctUntilChanged((a, b) => a?.status === b?.status),
      switchMap(s => {
        if (!s) { return throwError(() => new Error('This upload could not be found.')); }
        if (s.isProcessing) { return of({ stage: s.status as ExtractionStatus }); }
        if (!s.isReady) {
          return throwError(() => new Error(s.failureReason || 'We could not read this document.'));
        }
        // Read finished — now fetch what it produced.
        return this.getDraft(bidId, id).pipe(
          map(draft => ({ stage: 'ready' as ExtractionStatus, draft })),
        );
      }),
    );
  }

  /** GET bids/{bid}/vendor-quote-extractions/{id} — the whole draft, for the Review screen. */
  getDraft(bidId: number, id: number): Observable<VendorQuoteDraft | undefined> {
    return this.http.get<any>(this.baseURL.extraction(bidId, id)).pipe(
      map(res => VendorQuoteDataService.unwrap<VendorQuoteDraft | undefined>(res)),
      catchError(this.errorHandlerMessage),
    );
  }

  /**
   * Confirms the vendor from the vendor check (C1 add / C2 use existing).
   *
   * Sending no vendor id is the C1 answer — "not in the system, add it" — which the backend
   * records as a new vendor and creates on save.
   */
  confirmVendor(bidId: number, id: number, vendorId: number | null = null, logoDomain: string | null = null): Observable<VendorQuoteDraft | undefined> {
    // logo_domain: the website whose logo the user kept on the vendor check; stored on save.
    return this.http.post<any>(this.baseURL.extractionVendor(bidId, id), { vendor_id: vendorId, logo_domain: logoDomain }).pipe(
      map(res => VendorQuoteDataService.unwrap<VendorQuoteDraft | undefined>(res)),
      catchError(this.errorHandlerMessage),
    );
  }

  /**
   * Saves edits from Review.
   *
   * Header fields are sent as BARE values, not as {value, original, source} triples: the server
   * owns provenance and re-derives ✦/✎ by comparing against what the document said. Sending the
   * triple back would let the client overwrite the original and break the restore behaviour.
   */
  updateDraft(draft: VendorQuoteDraft): Observable<VendorQuoteDraft> {
    const body: any = {
      quote_number: draft.quoteNumber?.value ?? null,
      quote_date: draft.quoteDate?.value || null,
      valid_till: draft.validTill?.value || null,
      vendor: {
        name: draft.vendor?.name?.value ?? null,
        contact: draft.vendor?.contact?.value ?? null,
        email: draft.vendor?.email?.value ?? null,
        phone: draft.vendor?.phone?.value ?? null,
        address: draft.vendor?.address?.value ?? null,
      },
      terms: draft.terms ?? null,
      freight: draft.freight ?? 0,
      document_total: draft.documentTotal ?? null,
      quote_total_source: draft.quoteTotalSource ?? 'calculated',
      quote_total: draft.quoteTotalSource === 'manual' ? draft.quoteTotal ?? 0 : null,
      lines: (draft.lines ?? []).map(l => ({
        lineNo: l.lineNo,
        name: l.name,
        code: l.code || null,
        description: l.description || null,
        qty: l.qty?.value ?? 0,
        unit: l.unit || null,
        unitPrice: l.unitPrice?.value ?? 0,
        matchedProduct: l.matchedProduct ? { id: l.matchedProduct.id } : null,
        useDocTotal: !!l.useDocTotal,
        rejected: !!l.rejected,
      })),
      scopes: draft.scopes ?? [],
    };

    return this.http.patch<any>(this.baseURL.extraction(draft.bidId, draft.id), body).pipe(
      map(res => VendorQuoteDataService.unwrap<VendorQuoteDraft>(res)),
      catchError(this.errorHandlerMessage),
    );
  }

  /** Cancel on the vendor check or Review — nothing is kept. */
  deleteDraft(bidId: number, id: number): Observable<boolean> {
    return this.http.delete<any>(this.baseURL.extraction(bidId, id)).pipe(
      map(() => true),
      catchError(this.errorHandlerMessage),
    );
  }

  /**
   * Save Quote (D10): creates any new products, writes the real quote with its lines and scopes,
   * and retires the draft. Returns the SAVED QUOTE, not the draft — the Vendor Quotes tab renders
   * the new row straight from this response.
   */
  saveDraft(bidId: number, id: number): Observable<any> {
    return this.http.post<any>(this.baseURL.extractionSave(bidId, id), {}).pipe(
      map(res => VendorQuoteDataService.unwrap(res)),
      tap(saved => {
        this.justSavedId = saved?.id ?? null;
        this.setVendorQuoteUpdate(saved);
      }),
      catchError(this.errorHandlerMessage),
    );
  }

  /**
   * The bid's unfinished uploads — what a reloaded page resumes.
   *
   * Includes drafts already read and waiting on Review, not only ones still reading;
   * `isProcessing` / `isReady` on each tell the two apart.
   */
  getUnfinishedUploads(bidId: number): Observable<any[]> {
    return this.http.get<any>(this.baseURL.extractions(bidId)).pipe(
      map(res => (VendorQuoteDataService.unwrap<any[]>(res) ?? []) as any[]),
      catchError(this.errorHandlerMessage),
    );
  }

  /**
   * Vendor quotes that can price a proposal scope's lines (E1, F2, G1).
   *
   * The bid's quotes (typed in or uploaded — both are real `bid_vendor_quotes` rows, passed in from
   * `module/vendor_quote/16/{bid}`) matched to the scope's lines by product id in the browser. The plan's
   * optional `GET proposal-scopes/{id}/vendor-quote-availability` isn't needed while one request per bid
   * carries everything.
   */
  getScopeQuoteAvailability(bidId: number, lines: ProposalLineState[], realQuotes: any[] = []): Observable<QuoteSummary[]> {
    const option = (q: { quoteId: number; vendorId: number; vendorName: string; quotationReference: string; validTill: string },
      idx: number, productId: number, name: string, description: string, qty: number, unit: string, unitPrice: number): QuoteOption => ({
      // `idx` is the quote line's own id (bid_vendor_quote_products.id) — what the server's apply selects by.
      optionKey: `${q.quoteId}:${idx}`, quoteId: q.quoteId, quoteItemId: idx, vendorId: q.vendorId, vendorName: q.vendorName,
      quotationReference: q.quotationReference, validTill: q.validTill, isExpired: this.isExpired(q.validTill),
      hasPrice: unitPrice > 0, itemName: name, itemDescription: description, qty, unit, unitPrice, productId,
    });
    const summary = (q: { quoteId: number; vendorId: number; vendorName: string; quotationReference: string; validTill: string }, items: QuoteOption[], freight = 0): QuoteSummary => ({
      quoteId: q.quoteId, vendorId: q.vendorId, vendorName: q.vendorName, quotationReference: q.quotationReference, validTill: q.validTill,
      isExpired: this.isExpired(q.validTill), total: items.reduce((t, i) => t + i.qty * i.unitPrice, 0) + freight, items,
    });

    const out: QuoteSummary[] = [];

    // Products matched by product id; the rest are extras the scope doesn't have.
    for (const rq of realQuotes) {
      const q = { quoteId: Number(rq.id), vendorId: Number(rq.vendor?.id ?? 0), vendorName: rq.vendor?.name ?? 'No Vendor',
        quotationReference: rq.quotation_reference ?? '', validTill: rq.valid_till ?? '' };
      const items = (rq.products ?? [])
        .map((p: any, idx: number) => ({ p, idx }))
        // Materials only: a shipping line (item_type 4) is not a product the scope has, so "Apply to
        // Scope" would otherwise add the freight charge as a new material line.
        .filter(({ p }: any) => Number(p.item_type ?? 1) !== 4)
        .map(({ p, idx }: any) =>
          option(q, Number(p.quote_item_id) || idx, Number(p.product_id), p.name ?? '', p.description ?? '', Number(p.quantity) || 0, p.unit ?? '', Number(p.unit_price) || 0));
      if (items.length) out.push(summary(q, items));
    }
    return of(out);
  }

  // ---------- Vendor quotes on a proposal scope (Phase 6) ----------
  // The server rebuilds every plan from the choices sent here, prices included, so the screen never
  // pushes its own numbers. Errors carry the server's reason (expired quote, locked proposal, undo
  // conflict) as `Error.message`.

  // `target` picks the proposal-scope or change-order-scope endpoints; both answer in the same shapes.

  /** What applying would change — for Compare. Writes nothing. */
  previewVendorQuotes(scopeId: number, body: VendorQuoteApplyRequest, target: VendorQuoteTarget = 'proposal'): Observable<ServerApplyPlan> {
    const url = target === 'change-order' ? this.baseURL.coScopeQuotesPreview(scopeId) : this.baseURL.scopeQuotesPreview(scopeId);
    return this.http.post<any>(url, body).pipe(
      map(res => VendorQuoteDataService.unwrap<ServerApplyPlan>(res)),
      catchError(e => throwError(() => new Error(VendorQuoteDataService.reason(e)))),
    );
  }

  applyVendorQuotes(scopeId: number, body: VendorQuoteApplyRequest, target: VendorQuoteTarget = 'proposal'): Observable<VendorQuoteApplyResult> {
    const url = target === 'change-order' ? this.baseURL.coScopeQuotesApply(scopeId) : this.baseURL.scopeQuotesApply(scopeId);
    return this.http.post<any>(url, body).pipe(
      map(res => VendorQuoteDataService.unwrap<VendorQuoteApplyResult>(res)),
      catchError(e => throwError(() => new Error(VendorQuoteDataService.reason(e)))),
    );
  }

  undoVendorQuotes(scopeId: number, applicationId: number, target: VendorQuoteTarget = 'proposal'): Observable<VendorQuoteApplyResult> {
    const url = target === 'change-order' ? this.baseURL.coScopeQuotesUndo(scopeId) : this.baseURL.scopeQuotesUndo(scopeId);
    return this.http.post<any>(url, { application_id: applicationId }).pipe(
      map(res => VendorQuoteDataService.unwrap<VendorQuoteApplyResult>(res)),
      catchError(e => throwError(() => new Error(VendorQuoteDataService.reason(e)))),
    );
  }

  /** The apply still in effect on a scope, or null — brings the banner and marks back after a reload. */
  lastVendorQuoteApplication(scopeId: number, target: VendorQuoteTarget = 'proposal'): Observable<VendorQuoteApplication | null> {
    const url = target === 'change-order' ? this.baseURL.coScopeQuotesLast(scopeId) : this.baseURL.scopeQuotesLast(scopeId);
    return this.http.get<any>(url).pipe(
      // "No apply in effect" arrives as {data:{success, data:null}}; unwrap() would then fall back to the
      // envelope itself, which looks like an application. Only a row with an id is one.
      map(res => {
        const app = VendorQuoteDataService.unwrap<any>(res);
        return app?.id ? (app as VendorQuoteApplication) : null;
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * Every apply still in effect on a scope, newest first — the Apply dialog marks those quotes Applied
   * (with Undo) and shows single-line picks as "Used on". An error just means no rows: the dialog still opens.
   */
  vendorQuoteApplications(scopeId: number, target: VendorQuoteTarget = 'proposal'): Observable<VendorQuoteAppliedRow[]> {
    const url = target === 'change-order' ? this.baseURL.coScopeQuotesApplications(scopeId) : this.baseURL.scopeQuotesApplications(scopeId);
    return this.http.get<any>(url).pipe(
      map(res => {
        const rows = VendorQuoteDataService.unwrap<any>(res);
        return Array.isArray(rows) ? (rows as VendorQuoteAppliedRow[]) : [];
      }),
      catchError(() => of([])),
    );
  }

  /** The server's own words for a refusal: `{data:{message}}`, `{message}`, or the first field error. */
  private static reason(e: HttpErrorResponse): string {
    const body: any = e?.error ?? {};
    const errors = body?.data?.errors ?? body?.errors;
    const firstError = errors && typeof errors === 'object' ? Object.values(errors).flat()[0] : null;
    return body?.data?.message || body?.message || (typeof firstError === 'string' ? firstError : '')
      || 'Something went wrong. Please try again.';
  }

  private isExpired(validTill: string): boolean {
    if (!validTill) return false;
    // valid_till is free text in mixed formats ("2026-10-20", "03/31/2023", "2026-9-25 0:0:0"). Only an
    // ISO date gets a midday time (so it doesn't shift a day by timezone); appending one to "03/31/2023"
    // made an invalid date, which read as "not expired" while the server said expired.
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(validTill);
    const end = new Date(iso ? `${validTill}T12:00:00` : validTill.replace(/^(\d{4})-(\d{1,2})-(\d{1,2})\s.*$/, '$1/$2/$3'));
    if (isNaN(end.getTime())) return false;
    const today = new Date();
    return Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) < Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  }

  private daysFromToday(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

}
